/**
 * A aba Cenas — o livro-jogo do Mistik Island.
 *
 * <p>Dois painéis: a árvore Região › Área › Cenas à esquerda, a cena aberta à direita.
 * A geografia vem do conteúdo já cadastrado e aparece aqui só para pendurar cenas nela;
 * nada nesta tela escreve em região, área, local, NPC, criatura, item, evento ou quest.
 *
 * <p>Duas visões da mesma árvore, e o alternador troca entre elas. A <b>Lista</b>
 * responde "o que existe nesta área"; a <b>Árvore</b> responde "por onde se anda", que
 * é a pergunta que só o grafo sabe responder. As duas dividem busca, filtros e seleção
 * — trocar de visão muda o ângulo, não recomeça o trabalho.
 *
 * <p>A Lista fica montada mesmo quando escondida, com {@code hidden}: ela guarda o
 * rascunho das escolhas e a posição da rolagem, e remontá-la a cada ida e volta perderia
 * os dois.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroApi } from '../../../api/cliente';
import '../mundo.css';
import { ArvoreDeCenas, chaveDoNo } from './ArvoreDeCenas';
import * as cenasApi from './cenasApi';
import type {
  Cena,
  CenaNaArvore,
  PaginaDeCenas,
  ResumoDeCena,
  TipoDeCena,
} from './cenasTipos';
import { COR_DO_TIPO, ROTULO_DO_TIPO, TIPOS_DE_CENA } from './cenasTipos';
import { TituloDaPagina } from '../../../shell/TituloDaPagina';
import { EditorDeCena } from './EditorDeCena';
import { VisaoArvore } from './VisaoArvore';
import './cenas.css';

export function MundoCenas() {
  const fila = useQueryClient();

  const [selecionada, setSelecionada] = useState<number | null>(null);
  const [busca, setBusca] = useState('');
  const [tiposAtivos, setTiposAtivos] = useState<Set<TipoDeCena>>(new Set(TIPOS_DE_CENA));
  const [aberto, setAberto] = useState<Record<string, boolean>>({});
  /** Qual das duas visões está à frente. Elas dividem tudo o mais. */
  const [visao, setVisao] = useState<'lista' | 'arvore'>('lista');
  /** Ramos recolhidos na Árvore — estado dela, guardado aqui para a barra alcançar. */
  const [recolhidos, setRecolhidos] = useState<Record<string, boolean>>({});
  const [aviso, setAviso] = useState<{ texto: string; ok: boolean } | null>(null);
  /** A mensagem do 409, enquanto o time decide se exclui mesmo assim. */
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const {
    data: pagina,
    isLoading,
    error,
  } = useQuery<PaginaDeCenas>({ queryKey: ['cenas'], queryFn: cenasApi.pagina });

  const { data: cena } = useQuery<Cena>({
    queryKey: ['cenas', selecionada],
    queryFn: () => cenasApi.carregar(selecionada as number),
    enabled: selecionada != null,
  });

  function falhou(e: unknown) {
    setAviso({ texto: e instanceof Error ? e.message : 'Falha na operação.', ok: false });
  }

  const salvar = useMutation({
    mutationFn: (mudancas: Record<string, unknown>) =>
      cenasApi.salvar(selecionada as number, mudancas),
    onSuccess: () => void fila.invalidateQueries({ queryKey: ['cenas'] }),
    onError: falhou,
  });

  const criar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) => cenasApi.criar(corpo),
    onSuccess: async (nova) => {
      await fila.invalidateQueries({ queryKey: ['cenas'] });
      setSelecionada(nova.id);
    },
    onError: falhou,
  });

  /**
   * A ordem que o arrastar deixou.
   *
   * <p>Manda o nível inteiro e recebe a página de volta — o servidor pode ter criado
   * linhas de cena para nós que ainda não tinham, porque é nelas que a ordem mora, e
   * a tela precisa do desenho novo, não só do nó movido.
   */
  const reordenar = useMutation({
    mutationFn: ({ areaId, nivel }: { areaId: number; nivel: CenaNaArvore[] }) =>
      cenasApi.reordenar(
        areaId,
        nivel.map((n) => ({ tipo: n.tipo, alvoId: n.alvoId as number, nome: n.nome }))
      ),
    onSuccess: (nova) => fila.setQueryData(['cenas'], nova),
    onError: falhou,
  });

  const mover = useMutation({
    mutationFn: ({ id, direcao }: { id: number; direcao: 'cima' | 'baixo' }) =>
      cenasApi.mover(id, direcao),
    onSuccess: () => void fila.invalidateQueries({ queryKey: ['cenas'] }),
    onError: falhou,
  });

  const excluir = useMutation({
    mutationFn: (confirmado: boolean) => cenasApi.excluir(selecionada as number, confirmado),
    onSuccess: async () => {
      setConfirmando(null);
      setSelecionada(null);
      await fila.invalidateQueries({ queryKey: ['cenas'] });
    },
    // 409 não é falha: é a API pedindo confirmação, com o texto do que vai acontecer.
    onError: (e) =>
      e instanceof ErroApi && e.status === 409 ? setConfirmando(e.message) : falhou(e),
  });

  const todasAsCenas = useMemo(() => (pagina ? achatarPagina(pagina) : []), [pagina]);

  const destinosPossiveis = useMemo(
    () => todasAsCenas.filter((c) => c.id !== selecionada),
    [todasAsCenas, selecionada]
  );

  /*
   * NÃO EXISTE "CRIAR CENA", e é por isso que não há função aqui para isso.
   *
   * Área, local, NPC, criatura, coletável e evento SÃO cenas — o que existe no mundo já
   * está na árvore, escrito ou não. Pedir para criar uma cena era pedir para inventar um
   * lugar que o mundo não tem, e ainda dava um segundo caminho para a mesma coisa: a
   * cena avulsa nascia sem vínculo e ficava marcada com ⚠, esperando alguém apontá-la
   * para algo que já estava logo abaixo na mesma tela.
   *
   * Escrever uma cena é clicar no conteúdo dela. A linha em `cena` nasce nesse momento,
   * por baixo — ela guarda texto e escolhas, e nada mais.
   */

  /**
   * "Abrir tudo" e "fechar tudo" agem na visão que está à frente.
   *
   * <p>As duas guardam o estado de forma oposta, e é de propósito: na Lista o padrão é
   * fechado, então "aberto" é o que precisa ser listado; na Árvore só a região nasce
   * fechada, então é o "recolhido" que se registra. Um botão só para as duas evita
   * duas barras de ferramentas quase iguais.
   */
  /*
   * As chaves vêm de `chaveDoNo`, e não do id da cena.
   *
   * Elas precisam ser as MESMAS que a árvore usa para guardar aberto/fechado — e a
   * árvore passou a se indexar pela ficha (`locais-12`), porque o nó existe antes de
   * haver cena. Enquanto isto montava `c${cena.id}`, "Abrir tudo" abria as regiões e
   * as áreas e parava ali: os nós não recebiam a chave deles, e quem clicava via a
   * árvore meio aberta sem entender por quê.
   */
  function chavesDaPagina(): string[] {
    if (!pagina) return [];
    const chaves = ['prologo'];
    const descer = (nos: CenaNaArvore[]) =>
      nos.forEach((n) => {
        chaves.push(`c${chaveDoNo(n)}`);
        descer(n.filhas);
      });
    pagina.regioes.forEach((r) => {
      chaves.push(`r${r.id}`);
      r.areas.forEach((a) => {
        chaves.push(`a${a.id}`);
        descer(a.cenas);
      });
    });
    descer(pagina.semArea);
    return chaves;
  }

  function abrirTudo() {
    const mapa = Object.fromEntries(chavesDaPagina().map((c) => [c, visao === 'lista']));
    if (visao === 'lista') setAberto(mapa);
    else setRecolhidos(mapa);
  }

  function fecharTudo() {
    if (visao === 'lista') setAberto({});
    else setRecolhidos(Object.fromEntries(chavesDaPagina().map((c) => [c, true])));
  }

  /**
   * Abre um nó para escrever.
   *
   * <p>Se ele já tem cena, é só selecionar. Se não tem, a linha em {@code cena} nasce
   * agora, por baixo — apontando para a própria ficha do nó, sem formulário em branco e
   * sem pedir confirmação. Guardar texto é detalhe de armazenamento, e não uma criação
   * que se peça ao time para aprovar.
   */
  function abrirNo(no: CenaNaArvore, areaId: number) {
    if (no.id != null) {
      setSelecionada(no.id);
      return;
    }
    criar.mutate({
      nome: no.nome,
      tipo: no.tipo,
      areaId,
      ...(no.tipo === 'LOCAL' ? { localId: no.alvoId } : { alvoId: no.alvoId }),
    });
  }

  if (isLoading) return <div className="mundo mu-nada">Carregando…</div>;
  if (error || !pagina) {
    return (
      <div className="mundo mu-nada erro">
        {error instanceof Error ? error.message : 'Falha ao carregar as cenas.'}
      </div>
    );
  }

  const kpis = [
    { n: pagina.contagem.cenas, l: 'Cenas escritas', c: '#C9A25A' },
    { n: pagina.contagem.areas, l: 'Áreas no sistema', c: '#4E9C6B' },
    { n: pagina.contagem.ligacoes, l: 'Ligações (opções)', c: '#5B8FB9' },
  ];
  // "A escrever" substituiu "Sem vínculo", e não é a mesma coisa com outro nome:
  // aquilo era cena apontando para o vazio, um defeito; isto é conteúdo do mundo que
  // ainda não tem texto, que é trabalho combinado. Fica em vermelho pelo mesmo motivo
  // — é o que falta —, mas nasce alto e cai, em vez de nascer zero e subir.
  if (pagina.contagem.aEscrever > 0) {
    kpis.push({ n: pagina.contagem.aEscrever, l: 'A escrever', c: '#B04A45' });
  }

  return (
    <div className="mundo cn">
      <div className="mu-topo">
        <TituloDaPagina>
          O livro-jogo: o que o jogador vê, e as escolhas que o levam adiante. As fichas do
          mundo aparecem aqui só para vincular — nenhuma delas é alterada por esta tela.
        </TituloDaPagina>
        <div className="cn-kpis">
          {kpis.map((k) => (
            <div className="cn-kpi" key={k.l}>
              <div className="n" style={{ color: k.c }}>
                {k.n}
              </div>
              <div className="l">{k.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="cn-ferramentas">
        <input
          className="mu-busca"
          value={busca}
          placeholder="Buscar cena, código ou vínculo…"
          onChange={(e) => setBusca(e.target.value)}
        />
        <div className="cn-chips">
          {TIPOS_DE_CENA.map((t) => (
            <button
              key={t}
              type="button"
              className={`cn-chip${tiposAtivos.has(t) ? ' on' : ''}`}
              style={{ '--c': COR_DO_TIPO[t] } as React.CSSProperties}
              onClick={() => {
                const novos = new Set(tiposAtivos);
                if (novos.has(t)) novos.delete(t);
                else novos.add(t);
                setTiposAtivos(novos);
              }}
            >
              <span className="sw" />
              {ROTULO_DO_TIPO[t]}
            </button>
          ))}
        </div>
        <div className="cn-espaco" />

        {/* As duas visões dividem busca, filtros e seleção: trocar de visão não
            recomeça o trabalho, muda o ângulo. */}
        <div className="cn-visoes">
          <button
            type="button"
            className={visao === 'lista' ? 'on' : ''}
            onClick={() => setVisao('lista')}
          >
            ☰ Lista
          </button>
          <button
            type="button"
            className={visao === 'arvore' ? 'on' : ''}
            onClick={() => setVisao('arvore')}
          >
            ⌸ Árvore
          </button>
        </div>

        <button type="button" className="cn-tbtn" onClick={abrirTudo}>
          ⊞ Abrir tudo
        </button>
        <button type="button" className="cn-tbtn" onClick={fecharTudo}>
          ⊟ Fechar tudo
        </button>
      </div>

      {aviso && <div className={`cn-aviso${aviso.ok ? ' ok' : ''}`}>{aviso.texto}</div>}

      {confirmando && (
        <div className="cn-confirma">
          <p>{confirmando}</p>
          <div>
            <button type="button" className="cn-btn perigo" onClick={() => excluir.mutate(true)}>
              Excluir mesmo assim
            </button>
            <button type="button" className="cn-btn" onClick={() => setConfirmando(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {visao === 'arvore' && (
        <VisaoArvore
          pagina={pagina}
          selecionada={selecionada}
          // Clicar num cartão abre a cena no editor, que mora na Lista. É o mesmo
          // gesto do protótipo: a Árvore mostra o desenho, a Lista deixa escrever.
          aoSelecionar={(id) => {
            setSelecionada(id);
            setVisao('lista');
          }}
          busca={busca}
          tiposAtivos={tiposAtivos}
          recolhidos={recolhidos}
          aoRecolher={(chave, valor) =>
            setRecolhidos((antes) => ({ ...antes, [chave]: valor }))
          }
        />
      )}

      <div className="cn-paineis" hidden={visao !== 'lista'}>
        <section className="cn-painel">
          <header className="cn-painel-titulo">
            <span>ÁRVORE DE CENAS</span>
          </header>
          <ArvoreDeCenas
            pagina={pagina}
            selecionada={selecionada}
            aoAbrirNo={abrirNo}
            aoReordenar={(nivel, areaId) => reordenar.mutate({ areaId, nivel })}
            aoMover={(id, direcao) => mover.mutate({ id, direcao })}
            busca={busca}
            tiposAtivos={tiposAtivos}
            aberto={aberto}
            aoAbrir={(chave, valor) => setAberto((antes) => ({ ...antes, [chave]: valor }))}
          />
        </section>

        <section className="cn-painel">
          <header className="cn-painel-titulo">
            <span>EDITOR</span>
            {cena?.codigo && <span className="cn-conta">{cena.codigo}</span>}
          </header>
          {cena && selecionada != null ? (
            <EditorDeCena
              // Remontar ao trocar de cena é o que faz os campos não controlados
              // pegarem os valores da cena nova.
              key={cena.id}
              cena={cena}
              destinosPossiveis={destinosPossiveis}
              aoAplicar={(mudancas) => salvar.mutate(mudancas)}
              aoNovoPrologo={() => {
                const nome = window.prompt('Nome da cena de prólogo:');
                if (nome && nome.trim()) criar.mutate({ nome: nome.trim() });
              }}
              aoExcluir={() => excluir.mutate(false)}
              salvando={salvar.isPending}
            />
          ) : (
            <div className="cn-vazio">
              <b>Nenhuma cena aberta</b>
              Clique numa linha da árvore para escrever. Toda área, local, NPC, criatura,
              coletável e evento é uma cena — inclusive os que ainda não têm texto.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ bastidores

/**
 * Só entra quem já tem cena.
 *
 * <p>Esta lista alimenta o select de destino de uma escolha, e ele precisa de um id de
 * cena para apontar. Um nó ainda não escrito não tem para onde ser apontado —
 * oferecê-lo seria oferecer um destino que não existe.
 */
function achatar(nos: CenaNaArvore[], destino: ResumoDeCena[]) {
  for (const no of nos) {
    if (no.id != null) destino.push({ id: no.id, nome: no.nome });
    achatar(no.filhas, destino);
  }
}

/** Todas as cenas da página, em lista plana e por nome — é o que os selects mostram. */
function achatarPagina(pagina: PaginaDeCenas): ResumoDeCena[] {
  const todas: ResumoDeCena[] = [];
  pagina.regioes.forEach((r) => r.areas.forEach((a) => achatar(a.cenas, todas)));
  achatar(pagina.semArea, todas);
  return todas.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

